/**
 * CosyflowCard - Individual card component for displaying a cosyflow item
 * Converted from TypeScript for use in PromptusAI PWA
 */

import React, { useState, useRef, useEffect, memo, useCallback } from 'react';

// ============================================
// Default Icons (SVG components)
// ============================================

const ImageIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const VideoIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const AudioIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
  </svg>
);

const ThreeDIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
  </svg>
);

const TextIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ServerIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
  </svg>
);

// Action Button Icons
const PlayIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const ComfyUIIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TemplateIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
);

const InstallIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8"
    />
  </svg>
);

const ModifyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const DetailsIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const PlaygroundIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

// New Action Icons
const CloudUploadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CloudOfflineIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
  </svg>
);

// ============================================
// Default Configurations
// ============================================

const defaultOutputTypeConfig = {
  image: {
    icon: <ImageIcon />,
    color: 'bg-blue-500/20 text-blue-400',
    label: 'Image',
  },
  video: {
    icon: <VideoIcon />,
    color: 'bg-purple-500/20 text-purple-400',
    label: 'Video',
  },
  audio: {
    icon: <AudioIcon />,
    color: 'bg-green-500/20 text-green-400',
    label: 'Audio',
  },
  '3d': {
    icon: <ThreeDIcon />,
    color: 'bg-orange-500/20 text-orange-400',
    label: '3D',
  },
  text: {
    icon: <TextIcon />,
    color: 'bg-gray-500/20 text-gray-400',
    label: 'Text',
  },
};

const mediaBackgroundColors = {
  video: 'bg-gradient-to-br from-purple-900/80 to-purple-800/60',
  audio: 'bg-gradient-to-br from-green-900/80 to-green-800/60',
  '3d': 'bg-gradient-to-br from-orange-900/80 to-orange-800/60',
};

const defaultPlaceholder = (id) => {
  return `https://picsum.photos/seed/${id}/400/300`;
};

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  const lowercaseUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowercaseUrl.includes(ext));
};

// ============================================
// CosyflowCard Component
// ============================================

// Stats Icons
const SpeedIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const QualityIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);

const WorkersIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

// Status Indicator Icons
const StatusCloudIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-cyan' : 'text-white/25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
  </svg>
);

const StatusLocalIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-green-400' : 'text-white/25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const StatusWorkflowIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-purple-400' : 'text-white/25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
);

const StatusDistributedIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-orange-400' : 'text-white/25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

const StatusTrustedIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-yellow' : 'text-white/25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const StatusPublicIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-blue-400' : 'text-white/25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const StatusMineIcon = ({ active }) => (
  <svg className={`w-5 h-5 ${active ? 'text-yellow' : 'text-white/25'}`} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
  </svg>
);

export const CosyflowCard = memo(function CosyflowCard({
  item,
  isOwned = false,
  onClick,
  onRunInPlayground,
  onRunOfflineInPlayground,
  onRunInCosyUI,
  onRunInComfyUI,
  onOpenEditor,
  onCreateTemplate,
  onInstallLocally,
  isInstalling = false,
  onModifyCosyflow,
  onDeleteCosyflow,
  onViewDetails,
  onCreatorClick,
  onMakeAvailableLocally,
  onMakeAvailableOnline,
  onCreateFromDistributed,
  onTogglePublic,
  onToggleDistributed,
  displayCost,
  outputTypeConfig = defaultOutputTypeConfig,
  classNames = {},
  ctaText = 'Use This Cosyflow',
  getPlaceholderImage = defaultPlaceholder,
  renderImage,
  showActionButtons = true,
  showStats = false,
  showOpenEditor = true,
  availableModels = [],
  offlineModels = [],
  localAvailableModels = [],
  isStaffAccount = false,
  isStaffOrCreatorAccount = false,
  hasLocalServer = false,
}) {
  const [imageError, setImageError] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [showActions, setShowActions] = useState(null); // null, 'online', 'local', or 'mine'
  const [hoveredStatus, setHoveredStatus] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const cardRef = useRef(null);
  const menuBtnRef = useRef(null);
  const menuDropdownRef = useRef(null);
  const videoRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showActions) return;
    const handleClickOutside = (event) => {
      const clickedMenuBtn = menuBtnRef.current && menuBtnRef.current.contains(event.target);
      const clickedDropdown = menuDropdownRef.current && menuDropdownRef.current.contains(event.target);
      if (!clickedMenuBtn && !clickedDropdown) {
        setShowActions(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActions]);
  const isVideo = isVideoUrl(item.imageUrl);

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString();
  };

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    const video = videoRef.current;
    const maxDuration = 5;

    const handleTimeUpdate = () => {
      if (video.currentTime >= maxDuration) {
        video.currentTime = 0;
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [isVideo]);

  useEffect(() => {
    if (!cardRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsCompact(entry.contentRect.width < 280);
      }
    });

    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const outputType = item.outputType || 'image';
  const typeConfig = outputTypeConfig[outputType] || outputTypeConfig.image || defaultOutputTypeConfig.image;
  const hasValidImage = item.imageUrl && !item.imageUrl.includes('placeholder.png') && !imageError;
  const showMediaPlaceholder = !hasValidImage && (outputType === 'video' || outputType === 'audio' || outputType === '3d');

  // ============================================
  // Flag Computation for Action Visibility Matrix
  // ============================================

  // Flags from API data (cosyflows/download)
  const isPublic = item.isPublic === true;
  const isDistributed = item.distributed === true;
  const isTrusted = item.trusted === true;
  const isSameUser = isOwned; // Already passed as prop

  // Check availability for badges (your version)
  const isAvailableOnline = availableModels.includes(item.title);
  const isAvailableOffline = offlineModels.some(m => m.value === item.title);

  // Availability flags from colleague's version (from get-models APIs)
  const isAvailableLocally = localAvailableModels.length > 0 && localAvailableModels.includes(item.title);

  // Check if cosyflow has a workflow
  const hasWorkflow = item.hasComfyWorkflow === true;

  // Check if cosyflow title is available in the models list (for "Run in Playground" visibility)
  // Show button if available online, offline, or is a local cosyflow
  const isAvailableInPlayground = availableModels.length === 0 ||
                                   availableModels.includes(item.title) ||
                                   isAvailableOffline ||
                                   isAvailableLocally ||
                                   item.isLocal === true;

  // Check if card has any actionable badge
  const hasBadge = isOwned || isAvailableOnline || isAvailableOffline || isAvailableLocally;

  // ============================================
  // Action Visibility Logic Based on Matrix
  // ============================================

  // Action: Run Online Public CosyFlow in Playground
  const canRunOnlineInPlayground = isAvailableOnline;

  // Action: Run Offline CosyFlow in Playground
  const canRunOfflineInPlayground = isAvailableLocally;

  // Action: Load Distributed CosyFlow in CosyUI
  const canLoadDistributedInCosyUI = hasWorkflow;

  // Action: Load My CosyFlow in CosyUI
  const canLoadMyInCosyUI = isSameUser && hasWorkflow;

  // Combined: Show "Load in CosyUI" button if either condition is met
  const canLoadInCosyUI = canLoadDistributedInCosyUI || canLoadMyInCosyUI;

  // Action: Make My CosyFlow available locally
  const canMakeMyAvailableLocally = !isDistributed && isSameUser && !isAvailableLocally && !isAvailableOffline && hasWorkflow;

  // Action: Make My CosyFlow available online
  const canMakeMyAvailableOnline = !isDistributed && isSameUser && !isAvailableOnline && hasWorkflow;

  // Action: Make Public CosyFlow available locally (install distributed cosyflow)
  const canMakePublicAvailableLocally = isDistributed && isTrusted && !isAvailableLocally && !isAvailableOffline && hasWorkflow;

  // Combined: Show "Make Available Locally" if user's own or public distributed
  const canMakeAvailableLocally = canMakeMyAvailableLocally || canMakePublicAvailableLocally;

  // Action: Modify My Cosyflow
  const canModifyCosyflow = isSameUser;

  // Action: Create new Cosyflow from Distributed CosyFlow
  const canCreateFromDistributed = isDistributed || isSameUser;

  // Action: Modify My Template
  const canModifyTemplate = isSameUser;

  // Action: Create Template from Public online CosyFlow
  const canCreateTemplateFromPublicOnline = isAvailableOnline;

  // Action: Create Template from offline CosyFlow (user's own)
  const canCreateTemplateFromOffline = isSameUser;

  // Combined: Show "Create Template" button
  const canCreateTemplate = canCreateTemplateFromPublicOnline || canCreateTemplateFromOffline;

  const canInstallLocally = isDistributed && isTrusted && !item.isLocal && !isAvailableOffline && !isAvailableLocally && onInstallLocally

  const handleClick = () => {
    if (showActions) {
      setShowActions(null);
      return;
    }
    onClick?.(item);
  };

  const handleBadgeClick = (e, badgeType) => {
    e.stopPropagation();
    setShowActions(showActions === badgeType ? null : badgeType);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    const mode = showActions === 'local' ? 'offline' :
                 showActions === 'online' ? 'online' : null;
    setShowActions(null);
    action?.(item, { mode });
  };

  const handleImageError = useCallback((e) => {
    if (!e.target.dataset.fallback) {
      e.target.dataset.fallback = 'true';
      e.target.src = getPlaceholderImage(item.id || item.uid);
    } else {
      setImageError(true);
    }
  }, [getPlaceholderImage, item.id, item.uid]);

  const defaultRenderImage = useCallback(({ src, alt, className }) => (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={handleImageError}
    />
  ), [handleImageError]);

  const ImageComponent = renderImage || defaultRenderImage;

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className={`
        relative
        bg-dark-blue/50
        border border-white/20
        rounded-xl
        group cursor-pointer
        hover:border-yellow/50
        transition-all duration-300
        flex flex-col h-full
        ${classNames.card || ''}
      `}
    >
      {/* Menu Button - Top Left Corner */}
      <div ref={menuBtnRef} className="absolute top-2 left-2 z-30 pointer-events-auto">
        <div
          onClick={(e) => handleBadgeClick(e, 'menu')}
          className={`p-2 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-all cursor-pointer backdrop-blur-sm ${showActions === 'menu' ? 'ring-2 ring-yellow bg-black/80' : ''}`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </div>
      </div>

      {/* Media Container */}
      <div className={`relative aspect-video overflow-hidden rounded-t-xl ${classNames.mediaContainer || ''}`}>
        {/* Hover gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {showMediaPlaceholder ? (
          // Media type specific placeholder
          <div className={`absolute inset-0 ${mediaBackgroundColors[outputType] || 'bg-gray-800'} flex flex-col items-center justify-center`}>
            {outputType === 'video' && (
              <>
                <div className="w-14 h-14 rounded-full bg-purple-500/30 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <svg className="w-7 h-7 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <span className="text-purple-300 text-base font-medium">Video Cosyflow</span>
              </>
            )}
            {outputType === 'audio' && (
              <>
                <div className="w-14 h-14 rounded-full bg-green-500/30 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <AudioIcon />
                </div>
                <span className="text-green-300 text-base font-medium">Audio Cosyflow</span>
              </>
            )}
            {outputType === '3d' && (
              <>
                <div className="w-14 h-14 rounded-full bg-orange-500/30 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <ThreeDIcon />
                </div>
                <span className="text-orange-300 text-base font-medium">3D Cosyflow</span>
              </>
            )}
          </div>
        ) : (
          // Show image/video with media type overlay
          <>
            {isVideo && hasValidImage ? (
              <video
                ref={videoRef}
                src={item.imageUrl}
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${classNames.image || ''}`}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <ImageComponent
                src={hasValidImage ? item.imageUrl : getPlaceholderImage(item.id || item.uid)}
                alt={item.title}
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${classNames.image || ''}`}
              />
            )}

            {/* Play/media overlay for video/audio/3d with images */}
            {outputType === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center z-[5]">
                <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-purple-500/70 transition-colors">
                  <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
            {outputType === 'audio' && (
              <div className="absolute inset-0 flex items-center justify-center z-[5]">
                <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-green-500/70 transition-colors">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                  </svg>
                </div>
              </div>
            )}
            {outputType === '3d' && (
              <div className="absolute inset-0 flex items-center justify-center z-[5]">
                <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-orange-500/70 transition-colors">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                  </svg>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Action Buttons Overlay - Grouped Dropdown Menu */}
      <div className={`absolute top-12 left-2 z-30 transition-all duration-200 ease-out ${classNames.hoverOverlay || ''} ${showActions !== null ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`} ref={menuDropdownRef}>
        {showActionButtons ? (
          <div className="min-w-[200px] bg-[#1a1f2e] rounded-xl shadow-2xl border border-white/10 overflow-visible">
            {/* Menu Items */}
            <div className="py-1">
              {/* Run Group - First Segment */}
              {(canRunOnlineInPlayground || canRunOfflineInPlayground || canLoadInCosyUI || canInstallLocally) && (
                <>
                  {canRunOnlineInPlayground && onRunInPlayground && (
                    <button
                      onClick={(e) => handleActionClick(e, onRunInPlayground)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                    >
                      <PlaygroundIcon />
                      <span>Run Online in Playground</span>
                    </button>
                  )}
                  {canRunOfflineInPlayground && (onRunOfflineInPlayground || onRunInPlayground) && (
                    <button
                      onClick={(e) => handleActionClick(e, onRunOfflineInPlayground || onRunInPlayground)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                    >
                      <CloudOfflineIcon />
                      <span>Run Offline in Playground</span>
                    </button>
                  )}
                  {canLoadInCosyUI && onRunInCosyUI && isStaffOrCreatorAccount && (
                    <button
                      onClick={(e) => handleActionClick(e, onRunInCosyUI)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                    >
                      <ComfyUIIcon />
                      <span>Load in CosyUI</span>
                    </button>
                  )}
                  {canLoadInCosyUI && onRunInComfyUI && (
                    <div className="relative group/comfyui">
                      <button
                        onClick={(e) => hasLocalServer && handleActionClick(e, onRunInComfyUI)}
                        disabled={!hasLocalServer}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${hasLocalServer ? 'text-white/90 hover:bg-white/10' : 'text-white/30 cursor-not-allowed'}`}
                      >
                        <ComfyUIIcon />
                        <span>Load in ComfyUI</span>
                      </button>
                      {!hasLocalServer && (
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black/90 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/comfyui:opacity-100 pointer-events-none transition-opacity z-50">
                          Start a local ComfyUI server to enable this
                        </span>
                      )}
                    </div>
                  )}
                  {canInstallLocally && onInstallLocally && (
                    <div className="relative group/install">
                      <button
                        onClick={(e) => hasLocalServer && !isInstalling && handleActionClick(e, onInstallLocally)}
                        disabled={!hasLocalServer || isInstalling}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${!hasLocalServer ? 'text-white/30 cursor-not-allowed' : isInstalling ? 'text-white/90 opacity-50 cursor-not-allowed' : 'text-white/90 hover:bg-white/10'}`}
                      >
                        {isInstalling ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <InstallIcon />
                        )}
                        <span>{isInstalling ? 'Installing...' : 'Install Locally'}</span>
                      </button>
                      {!hasLocalServer && (
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black/90 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/install:opacity-100 pointer-events-none transition-opacity z-50">
                          Start a local ComfyUI server to enable this
                        </span>
                      )}
                    </div>
                  )}
                  <div className="border-t border-white/10 my-1" />
                </>              )}

              {/* Cosyflow Editor Submenu */}
              {canCreateFromDistributed && onCreateFromDistributed && (
                <div className="relative"
                  onMouseEnter={() => setOpenSubmenu('editor')}
                  onMouseLeave={() => setOpenSubmenu(null)}
                >
                  <button
                    className="w-full flex items-center justify-between px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <EditIcon />
                      <span>Edit</span>
                    </div>
                    <ChevronRightIcon />
                  </button>
                  {/* Submenu */}
                  {openSubmenu === 'editor' && (
                    <div className="absolute left-full top-0 -ml-2 pl-2 min-w-[180px]">
                      <div className="bg-[#1a1f2e] rounded-lg shadow-2xl border border-white/10 py-1">
                      <button
                        onClick={(e) => handleActionClick(e, onCreateFromDistributed)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                      >
                        <CopyIcon />
                        <span>Cosyflow Editor</span>
                      </button>
                      {/* Modify submenu item - hidden until fixed
                      {canModifyCosyflow && onModifyCosyflow && (
                        <button
                          onClick={(e) => handleActionClick(e, onModifyCosyflow)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                        >
                          <ModifyIcon />
                          <span>Modify</span>
                        </button>
                      )}
                      */}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Create Template - below Cosyflow Editor */}
              {canCreateTemplate && onCreateTemplate && (
                <button
                  onClick={(e) => handleActionClick(e, onCreateTemplate)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  <TemplateIcon />
                  <span>Create Template</span>
                </button>
              )}

              {/* Standalone Modify item - hidden until fixed
              {!canCreateFromDistributed && canModifyCosyflow && onModifyCosyflow && (
                <button
                  onClick={(e) => handleActionClick(e, onModifyCosyflow)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                >
                  <ModifyIcon />
                  <span>Modify</span>
                </button>
              )}
              */}

              {/* Details */}
              {onViewDetails && (
                <button
                  onClick={(e) => handleActionClick(e, onViewDetails)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-white/90 hover:bg-white/10 transition-colors"
                >
                  <DetailsIcon />
                  <span>Showcase</span>
                </button>
              )}

              {/* Date Info - Non-clickable */}
              {(() => {
                const createdFormatted = formatDate(item.created_date);
                const updatedFormatted = formatDate(item.updated_date);
                if (!createdFormatted && !updatedFormatted) return null;
                return (
                  <>
                    <div className="border-t border-white/10 my-1" />
                    {createdFormatted && (
                      <div className="w-full flex items-center justify-between px-4 py-2 text-white/50 text-sm">
                        <span>Created</span>
                        <span>{createdFormatted}</span>
                      </div>
                    )}
                    {updatedFormatted && (
                      <div className="w-full flex items-center justify-between px-4 py-2 text-white/50 text-sm">
                        <span>Updated</span>
                        <span>{updatedFormatted}</span>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Delete - Separated at bottom */}
              {(isOwned || item.isLocal) && onDeleteCosyflow && (
                <>
                  <div className="border-t border-white/10 my-1" />
                  <button
                    onClick={(e) => handleActionClick(e, onDeleteCosyflow)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    <DeleteIcon />
                    <span>Delete</span>
                  </button>
                </>              )}
            </div>
          </div>
        ) : (
          <span className={`px-4 py-2 bg-yellow text-dark-blue font-semibold rounded-lg shadow-lg ${classNames.ctaButton || ''}`}>
            {ctaText}
          </span>
        )}
      </div>

      {/* Status Badges */}
      <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-black/20 border-b border-white/5 flex-wrap relative z-20">
        <div
          className={`relative p-1 rounded cursor-default transition-all hover:scale-110 ${isOwned ? 'bg-yellow/30' : 'bg-white/5'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setHoveredStatus('mine')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          <StatusMineIcon active={isOwned} />
          {hoveredStatus === 'mine' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {isOwned ? 'My Cosyflow' : 'Not Mine'}
            </span>
          )}
        </div>
        <div
          className={`relative p-1 rounded cursor-default transition-all hover:scale-110 ${isAvailableOnline ? 'bg-cyan/30' : 'bg-white/5'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setHoveredStatus('online')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          <StatusCloudIcon active={isAvailableOnline} />
          {hoveredStatus === 'online' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {isAvailableOnline ? 'Available Online' : 'Not Available Online'}
            </span>
          )}
        </div>
        <div
          className={`relative p-1 rounded cursor-default transition-all hover:scale-110 ${isAvailableLocally ? 'bg-green-400/30' : 'bg-white/5'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setHoveredStatus('local')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          <StatusLocalIcon active={isAvailableLocally} />
          {hoveredStatus === 'local' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {isAvailableLocally ? 'Available Locally' : 'Not Available Locally'}
            </span>
          )}
        </div>
        <div
          className={`relative p-1 rounded cursor-default transition-all hover:scale-110 ${hasWorkflow ? 'bg-purple-400/30' : 'bg-white/5'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setHoveredStatus('workflow')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          <StatusWorkflowIcon active={hasWorkflow} />
          {hoveredStatus === 'workflow' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {hasWorkflow ? 'Has Workflow' : 'No Workflow'}
            </span>
          )}
        </div>
        <div
          className={`relative p-1 rounded transition-all hover:scale-110 ${isDistributed ? 'bg-orange-400/30' : 'bg-white/5'} ${isOwned && onToggleDistributed && hasWorkflow ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isOwned && onToggleDistributed && hasWorkflow) {
              onToggleDistributed(item, !isDistributed);
            }
          }}
          onMouseEnter={() => setHoveredStatus('distributed')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          {isOwned && onToggleDistributed ? (
            <svg className={`w-5 h-5 ${hasWorkflow ? (isDistributed ? 'text-orange-400' : 'text-white/40') : 'text-white/15'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="3" strokeWidth={2} />
              {isDistributed && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 12l4 4 6-7" />}
            </svg>
          ) : (
            <StatusDistributedIcon active={isDistributed} />
          )}
          {hoveredStatus === 'distributed' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {isOwned && onToggleDistributed
                ? (hasWorkflow
                  ? (isDistributed ? 'Click to un-distribute' : 'Click to distribute')
                  : 'Needs workflow to distribute')
                : (isDistributed ? 'Distributed' : 'Not Distributed')}
            </span>
          )}
        </div>
        <div
          className={`relative p-1 rounded cursor-default transition-all hover:scale-110 ${isTrusted ? 'bg-yellow/30' : 'bg-white/5'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setHoveredStatus('trusted')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          <StatusTrustedIcon active={isTrusted} />
          {hoveredStatus === 'trusted' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {isTrusted ? 'Trusted' : 'Not Trusted'}
            </span>
          )}
        </div>
        <div
          className={`relative p-1 rounded transition-all hover:scale-110 ${isPublic ? 'bg-blue-400/30' : 'bg-white/5'} ${isOwned && onTogglePublic ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isOwned && onTogglePublic) {
              onTogglePublic(item, !isPublic);
            }
          }}
          onMouseEnter={() => setHoveredStatus('public')}
          onMouseLeave={() => setHoveredStatus(null)}
        >
          {isOwned && onTogglePublic ? (
            <svg className={`w-5 h-5 ${isPublic ? 'text-blue-400' : 'text-white/40'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="3" strokeWidth={2} />
              {isPublic && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 12l4 4 6-7" />}
            </svg>
          ) : (
            <StatusPublicIcon active={isPublic} />
          )}
          {hoveredStatus === 'public' && (
            <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-xs rounded whitespace-nowrap z-50 shadow-lg border border-white" style={{ color: '#fff' }}>
              {isOwned && onTogglePublic
                ? (isPublic ? 'Click to make private' : 'Click to make public')
                : (isPublic ? 'Public' : 'Private')}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={`p-4 flex flex-col flex-grow ${classNames.content || ''}`}>
        <h3 className={`text-lg font-semibold text-white group-hover:text-yellow transition-colors line-clamp-2 ${classNames.title || ''}`} title={item.title}>
          {item.title}
        </h3>
        <p className={`text-base text-white/70 line-clamp-2 mt-1.5 ${classNames.description || ''}`}>
          {item.description || 'No description available'}
        </p>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-yellow/10 border border-yellow/20 rounded-full text-xs text-yellow/80"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats Row */}
        {showStats && (item.speed !== undefined || item.quality !== undefined || item.popularity !== undefined || item.workers !== undefined || item.category) && (
          <div className="flex items-center justify-between mt-2">
            <div className="flex flex-wrap gap-2">
              {item.speed !== undefined && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow/10 rounded text-sm text-yellow" title="Speed">
                  <SpeedIcon />
                  <span>{item.speed}%</span>
                </div>
              )}
              {item.quality !== undefined && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-cyan/10 rounded text-sm text-cyan" title="Quality">
                  <QualityIcon />
                  <span>{item.quality}%</span>
                </div>
              )}
              {item.workers !== undefined && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/10 rounded text-sm text-purple-400" title="Workers">
                  <WorkersIcon />
                  <span>{item.workers}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Spacer to push footer to bottom */}
        <div className="flex-grow" />

        {/* Category Badge - always right-aligned above footer */}
        {item.category && (
          <div className="flex justify-end mb-2">
            <span className="px-2 py-0.5 bg-white/10 border border-white/20 rounded text-sm text-white/70">
              {item.category}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className={`flex items-center justify-between mt-3 pt-3 border-t border-white/10 ${classNames.footer || ''}`}>
          {item.creator ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCreatorClick?.({ creator: item.creator, accountNo: item.accountNo });
              }}
              className={`text-sm text-white/50 hover:text-yellow hover:underline transition-colors ${classNames.creator || ''}`}
            >
              by {item.creator}
            </button>
          ) : (
            <span className={`text-sm text-white/50 ${classNames.creator || ''}`}></span>
          )}
          {displayCost && (
            <span className={`text-sm font-medium text-yellow ${classNames.cost || ''}`}>
              {displayCost} credits
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

export default CosyflowCard;
